import { useCallback, useEffect, useRef, useState } from 'react'
import { useForm } from 'react-final-form-hooks'

import { isAxiosError } from 'axios'
import { useTheme } from 'store/theme'

import { ErrorResponse, SaveCardPaymentMethod } from '@infura/packages.api'
import Notification from '@infura/packages.components/Notification'
import { Button } from '@infura/packages.ui/Button'
import { Loader } from '@infura/packages.ui/Loader'
import { Text } from '@infura/packages.ui/Text'

import useCard from 'hooks/useCard'

import { api } from 'lib/api/api'
import { FormBackground, checkIframes, getChargify, getChargifyLoadConfig } from 'lib/utils/chargify'

interface Props {
  onCancel?: () => void
  onSuccess?: (card: SaveCardPaymentMethod) => void
  onError?: (error: any) => void
  ctaText?: string
  showHeadings?: boolean
  showErrorBanner?: boolean
  formBackground?: FormBackground
}

const ChargifyCCForm = ({
  ctaText = 'Update Billing Info',
  formBackground = 'default',
  onCancel,
  onError,
  onSuccess,
  showErrorBanner = true,
  showHeadings = true
}: Props) => {
  const { mutate: mutateCard } = useCard()
  const chargifyForm = useRef<HTMLFormElement>()
  const [error, setError] = useState('')
  const [processingToken, setProcessingToken] = useState(false)
  const [showLoaders, setShowLoaders] = useState(true)

  const handleToken = useCallback(
    async (token: string) => {
      const {
        data: { data: paymentMethod }
      } = await api.upsertCard({ token: { chargify_token: token } })
      const {
        data: { data: card }
      } = await api.getChargifyCard()
      mutateCard({ data: card })
      if (onSuccess) onSuccess(paymentMethod)
    },
    [mutateCard, onSuccess]
  )

  const onSubmit = useCallback(async () => {
    setError('')
    if (onError) onError('')
    if (!getChargify()) return
    setProcessingToken(true)
    await getChargify().token(
      chargifyForm.current,
      async (token: string) => {
        await handleToken(token)
        setProcessingToken(false)
      },
      (e: any) => {
        if (isAxiosError(e)) {
          const { error } = e.response?.data as ErrorResponse
          // @ts-expect-error enabled to support strictNullChecks - Please fix this if you can!
          setError(error)
          if (onError) onError(error?.message || error)
        } else {
          setError(e?.errors ? 'Your card was declined' : 'Please provide payment details')
          if (onError) onError(e?.errors ? 'Your card was declined' : 'Please provide payment details')
        }
        setProcessingToken(false)
      }
    )
  }, [setProcessingToken, handleToken, setError, onError])

  const theme = useTheme()
  useEffect(() => {
    const iframeLoadConfig = getChargifyLoadConfig(theme, formBackground)
    getChargify()?.load(iframeLoadConfig)
    checkIframes(chargifyForm, setShowLoaders)
  }, [formBackground, theme])

  const { handleSubmit, submitting } = useForm({ onSubmit })

  return (
    <div className='flex flex-1 flex-col'>
      {showErrorBanner && error ? (
        <Notification className='mb-4' color='alert' size='sm' variant='outlined'>
          {/* @ts-expect-error enabled to support strictNullChecks - Please fix this if you can! */}
          <span className='w-full text-center'>{error?.message || error}</span>
        </Notification>
      ) : null}
      <div className='chargify-js-wrapper'>
        <div className='chargify-js-content'>
          {/* @ts-expect-error enabled to support strictNullChecks - Please fix this if you can! */}
          <form className='host-form' id='chargify-form' ref={chargifyForm}>
            {showLoaders ? (
              <div className='px-2'>
                {showHeadings ? <Text className='mb-4 text-sm'>Payment Details</Text> : null}
                <div className='cardfront overflow-hidden'>
                  <Loader className='mb-8 h-9' pulse /> {/* Card Number */}
                </div>
                <div className='cardback grid grid-cols-1 gap-x-4 overflow-hidden md:grid-cols-3'>
                  <Loader className='mb-8 h-9' pulse /> {/* Expiry Month */}
                  <Loader className='mb-8 h-9' pulse /> {/* Expiry Year */}
                  <Loader className='mb-8 h-9' pulse /> {/* CVV */}
                </div>
                <div className='name grid grid-cols-1 gap-x-2 overflow-hidden md:grid-cols-2'>
                  <Loader className='mb-8 h-9' pulse /> {/* First Name */}
                  <Loader className='mb-8 h-9' pulse /> {/* Last Name */}
                </div>
                {showHeadings ? <Text className='mb-4 text-sm'>Billing Info</Text> : null}
                <Loader className='mb-8 h-9' pulse /> {/* Address Line 1 */}
                <Loader className='mb-8 h-9' pulse /> {/* Address Line 2 */}
                <div className='country-region grid grid-cols-1 gap-x-2 overflow-hidden md:grid-cols-2'>
                  <Loader className='mb-8 h-9' pulse /> {/* Country */}
                  <Loader className='mb-8 h-9' pulse /> {/* Region/State */}
                </div>
                <div className='city-zip grid grid-cols-1 gap-x-2 overflow-hidden md:grid-cols-2'>
                  <Loader className='mb-8 h-9' pulse /> {/* City */}
                  <Loader className='mb-8 h-9' pulse /> {/* ZIP */}
                </div>
              </div>
            ) : null}
            {/* Chargify Fields */}
            <div className={showLoaders ? 'sr-only' : 'not-sr-only'}>
              {showHeadings ? <Text className='mb-2 px-2 text-sm'>Payment Details</Text> : null}
              <div className='cardfront overflow-hidden md:h-[92px]'>
                <div id='chargify1'></div>
              </div>
              <div className='cardback grid grid-cols-1 overflow-hidden md:h-[92px] md:grid-cols-3'>
                <div className='grid-col flex grow-0 items-start' id='chargify2'></div>
                <div className='grid-col flex grow-0 items-start' id='chargify3'></div>
                <div className='grid-col flex grow-0 items-start' id='chargify4'></div>
              </div>
              <div className='name grid grid-cols-1 overflow-hidden md:h-[92px] md:grid-cols-2'>
                <div className='grid-col flex grow-0 items-start' id='chargify5'></div>
                <div className='grid-col flex grow-0 items-start' id='chargify6'></div>
              </div>
              {showHeadings ? <Text className='mb-2 px-2 text-sm '>Billing Info</Text> : null}
              <div className='address1 overflow-hidden md:h-[92px]'>
                <div id='chargify7'></div>
              </div>
              <div className='address2 overflow-hidden md:h-[92px]'>
                <div id='chargify8'></div>
              </div>
              <div className='address3 grid grid-cols-1 overflow-hidden md:h-[92px] md:grid-cols-2'>
                <div className='grid-col flex grow-0 items-start' id='chargify9'></div>
                <div className='grid-col flex grow-0 items-start' id='chargify10'></div>
              </div>
              <div className='address4 grid grid-cols-1 overflow-hidden md:h-[92px] md:grid-cols-2'>
                <div className='grid-col flex grow-0 items-start' id='chargify11'></div>
                <div className='grid-col flex grow-0 items-start' id='chargify12'></div>
              </div>
              <div className='flex justify-end gap-4 text-right'>
                {onCancel ? (
                  <Button
                    data-testid='chargify-form-cancel'
                    onClick={() => {
                      setError('')
                      if (onError) onError('')
                      if (onCancel) onCancel()
                    }}
                    theme='secondary'
                  >
                    Cancel
                  </Button>
                ) : null}
                <Button
                  className='host-button'
                  data-testid='chargify-form-save-card'
                  disabled={submitting || processingToken}
                  loading={processingToken}
                  onClick={e => {
                    e.preventDefault()
                    e.stopPropagation()
                    handleSubmit()
                  }}
                  preserveTextWhileLoading={true}
                  type='button'
                >
                  {ctaText}
                </Button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

export default ChargifyCCForm
